import React from "react";
import { Link } from "react-router-dom";
import { Twitter, Linkedin, Facebook, Instagram, Youtube, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import { useSite } from "@/context/SiteContext";

export default function PublicFooter() {
  const { site, trackAndGo } = useSite();
  const social = site.social || {};
  const regulators = (site.regulators || "").split("\n").map((r) => r.trim()).filter(Boolean);
  const socials = [
    { key: "twitter", Icon: Twitter },
    { key: "linkedin", Icon: Linkedin },
    { key: "facebook", Icon: Facebook },
    { key: "instagram", Icon: Instagram },
    { key: "youtube", Icon: Youtube },
  ];

  return (
    <footer className="bg-[#06122E] border-t border-[#203E70] pt-16 pb-8" data-testid="public-footer">
      <div className="cb-container">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-8">
          <div className="col-span-2">
            <Logo />
            <p className="text-slate-400 text-sm mt-4 max-w-xs leading-relaxed">
              {site.tagline || "Global Institutional Trading & Private Liquidity Markets"}
            </p>
            <div className="flex gap-3 mt-5">
              {socials.map(({ key, Icon }) =>
                social[key] ? (
                  <a key={key} href={social[key]} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-[#203E70] flex items-center justify-center text-slate-300 hover:text-[#D8AE4E] hover:border-[#D8AE4E]/60 transition-colors" data-testid={`social-${key}`}>
                    <Icon size={16} />
                  </a>
                ) : null
              )}
            </div>
          </div>

          <FooterCol title="Trading" links={[["Account Types", "/accounts"], ["Trading Conditions", "/conditions"], ["Leverage & Margin", "/conditions"], ["Spreads & Swaps", "/conditions"], ["Markets", "/markets"]]} />
          <FooterCol title="Platforms" links={[["MetaTrader 4", "/platforms"], ["MetaTrader 5", "/platforms"], ["WebTrader", "/platforms"], ["Mobile Apps", "/platforms"], ["Automated Trading", "/platforms"]]} />
          <FooterCol title="Markets" links={[["Forex", "/markets"], ["Metals", "/markets"], ["Indices", "/markets"], ["Commodities", "/markets"], ["Stocks", "/markets"], ["Crypto", "/markets"]]} />
          <FooterCol title="Research" links={[["News & Analysis", "/news"], ["Trading Ideas", "/news"], ["Economic Calendar", "/news"], ["Education Center", "/education"], ["FAQ", "/faq"]]} />
          <FooterCol title="Company" links={[["Promotions", "/promotions"], ["Contact", "/contact"], ["Accounts", "/accounts"], ["Conditions", "/conditions"]]} />
        </div>

        {regulators.length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#203E70]" data-testid="footer-regulation-band">
            <h4 className="overline mb-5 text-center">Regulation & Audit</h4>
            <div className="flex flex-wrap justify-center gap-4">
              {regulators.map((r, i) => (
                <div key={i} className="cb-card !bg-[#0B1D40] px-6 py-3 text-sm text-slate-300 flex items-center gap-2" data-testid={`regulator-${i}`}>
                  <ShieldCheck size={15} className="gold-text" /> {r}
                </div>
              ))}
            </div>
          </div>
        )}

        {(site.risk_warning || site.footer_disclaimer) && (
          <div className="mt-12 pt-8 border-t border-[#203E70] text-xs text-slate-500 leading-relaxed space-y-3">
            {site.risk_warning && <p data-testid="footer-risk-warning">{site.risk_warning}</p>}
            {site.footer_disclaimer && <p>{site.footer_disclaimer}</p>}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-[#203E70] text-xs" data-testid="footer-legal-row">
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-slate-500">
            {["Terms of Use", "Privacy Policy", "Cookie Policy", "Risk Disclosure", "Order Execution Policy", "Complaints"].map((x) => (
              <Link key={x} to="/conditions" className="hover:text-[#D8AE4E] transition-colors">{x}</Link>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#203E70] flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Cresent Bank. All rights reserved.</span>
          <Link to="/admin/login" className="hover:text-[#D8AE4E] transition-colors" data-testid="footer-admin-link">Admin Panel</Link>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="overline mb-4">{title}</h4>
      <div className="space-y-2">
        {links.map(([label, to]) => (
          <Link key={label} to={to} className="block text-sm text-slate-400 hover:text-[#D8AE4E] transition-colors">
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
}
